"use client";

import { AIDisclaimerModal } from "@/components/app/ai-disclaimer-modal";
import { createClient } from "@masterkey/supabase/client";
import { type ReactNode, createContext, useContext, useState } from "react";

interface AIDisclaimerState {
  hasAcknowledgedDisclaimer: boolean;
}

interface AIDisclaimerContextType extends AIDisclaimerState {
  showDisclaimer: () => Promise<boolean>;
}

const AIDisclaimerContext = createContext<AIDisclaimerContextType | undefined>(
  undefined,
);

interface AIDisclaimerProviderProps {
  children: ReactNode;
  initialState: AIDisclaimerState;
}

export function AIDisclaimerProvider({
  children,
  initialState,
}: AIDisclaimerProviderProps) {
  const [hasAcknowledgedDisclaimer, setHasAcknowledgedDisclaimer] = useState(
    initialState.hasAcknowledgedDisclaimer,
  );
  const [isOpen, setIsOpen] = useState(false);
  const supabase = createClient();

  const showDisclaimer = async (): Promise<boolean> => {
    if (hasAcknowledgedDisclaimer) return true;

    return new Promise((resolve) => {
      setIsOpen(true);

      const handleAcknowledge = async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          setIsOpen(false);
          resolve(false);
          return;
        }

        const { error } = await supabase
          .from("users")
          .update({ acknowledged_ai_disclaimer_date: new Date().toISOString() })
          .eq("id", user.id);

        if (!error) {
          setHasAcknowledgedDisclaimer(true);
          setIsOpen(false);
          resolve(true);
        } else {
          console.error("Error updating disclaimer acknowledgment:", error);
          setIsOpen(false);
          resolve(false);
        }
      };

      const handleClose = () => {
        setIsOpen(false);
        resolve(false);
      };

      return (
        <AIDisclaimerModal
          isOpen={isOpen}
          onAcknowledge={handleAcknowledge}
          onClose={handleClose}
        />
      );
    });
  };

  return (
    <AIDisclaimerContext.Provider
      value={{
        hasAcknowledgedDisclaimer,
        showDisclaimer,
      }}
    >
      {children}
      {isOpen && (
        <AIDisclaimerModal
          isOpen={isOpen}
          onAcknowledge={async () => {
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (!user) {
              setIsOpen(false);
              return;
            }

            const { error } = await supabase
              .from("users")
              .update({
                acknowledged_ai_disclaimer_date: new Date().toISOString(),
              })
              .eq("id", user.id);

            if (!error) {
              setHasAcknowledgedDisclaimer(true);
              setIsOpen(false);
            } else {
              console.error("Error updating disclaimer acknowledgment:", error);
              setIsOpen(false);
            }
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </AIDisclaimerContext.Provider>
  );
}

export function useAIDisclaimer() {
  const context = useContext(AIDisclaimerContext);
  if (context === undefined) {
    throw new Error(
      "useAIDisclaimer must be used within an AIDisclaimerProvider",
    );
  }
  return context;
}
